import { useEffect, useRef, useState } from "react"
import AdminLayout from "@/components/admin/AdminLayout"
import { getAdminPartners } from "@/api/partnerApi"
import { Card, CardContent } from "@/components/ui/card"

export default function AdminMapPage() {
    const mapRef = useRef<HTMLDivElement>(null)
    const [partners, setPartners] = useState<any[]>([])

    // 1. 전국 파트너 데이터 모두 불러오기
    useEffect(() => {
        const fetchAllPartners = async () => {
            try {
                // 지도에 뿌리기 위해 페이징 없이 최대치(예: 1000건)를 한 번에 불러옵니다.
                const res = await getAdminPartners(1, 1000)
                if (res?.data?.content) {
                    setPartners(res.data.content)
                }
            } catch (error) {
                console.error("화원 데이터를 불러오는데 실패했습니다.", error)
            }
        }
        fetchAllPartners()
    }, [])

    // 2. 카카오맵 렌더링 및 마커 클러스터링 적용
    useEffect(() => {
        // 데이터가 아직 없거나 map을 그릴 div가 준비되지 않았다면 대기
        if (!mapRef.current || partners.length === 0) return

        const kakao = (window as any).kakao
        // if (!kakao || !kakao.maps) return

        // 💡 카카오 스크립트 자체를 못 불러왔다면 에러 띄우기
        if (!kakao || !kakao.maps) {
            console.error("카카오맵 스크립트를 찾을 수 없습니다. index.html의 API 키를 확인해주세요.")
            return
        }

        // 카카오맵은 비동기로 로드되므로 load 콜백 안에서 실행해야 안전합니다.
        kakao.maps.load(() => {
            // 지도 초기 옵션 (중심좌표: 대한민국 중심부, 확대 레벨: 12)
            // const mapOption = {
            //     center: new kakao.maps.LatLng(36.2683, 127.6358),
            //     level: 12
            // }

            // 💡 1. 화원 데이터가 있든 없든 일단 '빈 지도'부터 무조건 그립니다!
            const mapOption = {
                center: new kakao.maps.LatLng(36.2683, 127.6358),
                level: 12
            }

            // 지도 생성
            const map = new kakao.maps.Map(mapRef.current, mapOption)

            // 💡 2. 파트너 데이터가 아직 안 들어왔다면 (또는 0건이면) 여기서 로직 종료
            if (partners.length === 0) return

            // 마커 클러스터러 생성 (마커가 뭉쳐있을 때 숫자로 표시해주는 기능)
            const clusterer = new kakao.maps.MarkerClusterer({
                map: map,
                averageCenter: true,
                minLevel: 10 // 레벨 10 이상 축소되었을 때 클러스터링 적용
            })

            // 파트너 데이터로 마커 및 인포윈도우(팝업) 생성
            const markers = partners
                .filter(p => p.lat !== null && p.lng !== null) // 💡 명시적으로 null 체크
                .map(p => {
                    // const position = new kakao.maps.LatLng(p.lat, p.lng)
                    const position = new kakao.maps.LatLng(p.lat, p.lng);
                    const marker = new kakao.maps.Marker({ position });

                    // 💡 클릭 시 뜰 말풍선(인포윈도우) HTML 디자인
                    const content = `
                        <div style="padding:16px; border-radius:16px; min-width:240px; border: 1px solid #e2e8f0; background: #fff; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);">
                            <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 8px;">
                                <h4 style="font-weight:900; font-size:16px; color:#0f172a; margin:0;">${p.partnerName}</h4>
                                <span style="font-size:11px; padding: 2px 8px; border-radius: 99px; background: ${p.status === 'ACTIVE' ? '#dcfce7' : '#ffe4e6'}; color: ${p.status === 'ACTIVE' ? '#166534' : '#be123c'}; font-weight: bold;">
                                    ${p.status === 'ACTIVE' ? '운영중' : '정지'}
                                </span>
                            </div>
                            <p style="font-size:12px; color:#64748b; margin:0 0 12px 0; line-height: 1.4;">${p.address} ${p.detailAddress || ''}</p>
                            <div style="font-size:13px; color:#334155; background: #f8fafc; padding: 8px; border-radius: 8px;">
                                <div style="margin-bottom:4px;"><strong>담당자:</strong> ${p.managerName || '-'}</div>
                                <div><strong>연락처:</strong> <a href="tel:${p.managerMobile}" style="color: #2563eb; text-decoration: none;">${p.managerMobile || '-'}</a></div>
                            </div>
                        </div>
                    `

                    const infowindow = new kakao.maps.InfoWindow({
                        content: content,
                        removable: true // x 버튼 표시
                    })

                    // 마커에 클릭 이벤트 등록
                    kakao.maps.event.addListener(marker, 'click', function() {
                        infowindow.open(map, marker)
                    })

                    return marker
                })

            // 완성된 마커들을 클러스터러에 일괄 추가
            clusterer.addMarkers(markers)
        })
    }, [partners])

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div>
                    <h2 className="text-3xl font-bold">전국 화원 지도</h2>
                    <p className="mt-2 text-sm text-slate-500">
                        전국에 등록된 <span className="font-bold text-blue-600">{partners.length}</span>개의 파트너 화원 분포와 상세 정보를 지도에서 확인합니다.
                    </p>
                </div>

                <Card className="rounded-[28px] border border-slate-200 shadow-sm overflow-hidden">
                    <CardContent className="p-0 relative">
                        {/* 💡 지도가 그려질 도화지 */}
                        <div ref={mapRef} className="w-full h-[700px] bg-slate-100 flex items-center justify-center">
                            <span className="text-slate-400">지도를 불러오는 중입니다...</span>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    )
}